/**
 * Same-origin guard for user-controllable URL inputs (e.g. `?continueUrl=…`,
 * `?redirect=…`). Accepts relative paths and absolute URLs on the current origin;
 * returns null for anything else — blocks open-redirect attacks where an attacker
 * stages `?continueUrl=https://evil.com` against a victim. Absolute same-origin
 * URLs are normalized to relative so downstream callers only ever see one shape.
 *
 * Rejected: protocol-relative (`//evil.com` inherits the parent scheme),
 * cross-origin URLs, non-http schemes (`javascript:`, `data:`, …), and
 * malformed strings.
 */
export const sanitizeSameOriginPath = (path: string | null | undefined): string | null => {
  if (!path) return null;
  if (path.startsWith('//')) return null;
  if (path.startsWith('/')) return path;
  try {
    const parsed = new URL(path);
    if (parsed.origin === window.location.origin) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }
  } catch {
    // Not a parseable absolute URL — fall through to reject.
  }
  return null;
};

/**
 * Resolves `path` against `base` and returns the absolute URL **only when it is a plain http(s)
 * address** — otherwise null. Use it to guard a `window.location.href = …` navigation whose base is
 * dynamic / user-controllable (e.g. a single-tenant redirect target read from the DB): a
 * `javascript:` or `data:` value assigned to `location.href` executes as script (XSS), so callers
 * must navigate only on a non-null result.
 */
export const toHttpUrl = (path: string, base: string): URL | null => {
  let resolved: URL;
  try {
    resolved = new URL(path, base);
  } catch {
    return null;
  }
  return resolved.protocol === 'https:' || resolved.protocol === 'http:' ? resolved : null;
};
